/* Kreo Web Notifications — storefront widget (config-driven).
 * Loaded deferred from the theme; reads page context from window.__WEBNOTI
 * (snippets/webnoti-optin.liquid) and behavioral config from the worker
 * (GET /apps/webnoti/config). Registers the SW same-origin via the App Proxy.
 * Arms only the enabled, audience-matched triggers and enforces re-prompt caps.
 * No external dependencies. */
(function () {
  var CFG = window.__WEBNOTI || {};
  var PROXY = CFG.proxy || '/apps/webnoti';
  var VAPID = CFG.vapid;
  var PRODUCT_ID = CFG.productId ? String(CFG.productId) : '';
  var CUSTOMER_ID = CFG.customerId ? String(CFG.customerId) : '';
  var ORDERS_COUNT = parseInt(CFG.ordersCount, 10) || 0; // logged-in customer's order count
  var PRODUCT_SOLD_OUT = !!PRODUCT_ID && CFG.productAvailable === false;

  var supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!supported || !VAPID) return;

  // Storage keys for frequency caps.
  var K_DISMISS = 'webnoti_dismiss_count';
  var K_SNOOZE = 'webnoti_snooze_until';
  var K_SEEN = 'webnoti_seen';
  var K_SESSION = 'webnoti_session_shown';

  // Only one opt-in pill per page load, whichever trigger fires first.
  var prompted = false;

  // The one opt-in/watch bar currently on screen (the host element). Mounting a
  // new bar removes the previous one so two pills never stack.
  var activeBar = null;
  function mountBar(host) {
    if (activeBar) { try { activeBar.remove(); } catch (e) {} }
    activeBar = host;
    document.body.appendChild(host);
  }

  function urlB64ToUint8(base64) {
    var pad = '='.repeat((4 - (base64.length % 4)) % 4);
    var b = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
    var raw = atob(b);
    var out = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  }

  async function registerSw() {
    // The SW is served from the App Proxy path (/apps/webnoti/sw.js). Shopify's
    // App Proxy STRIPS the Service-Worker-Allowed header, so we cannot claim
    // scope '/' — the browser rejects that with a SecurityError. Register at the
    // script's own scope (default: /apps/webnoti/); push delivery is not
    // scope-limited, so notifications still work. Since this SW does not control
    // the current page, navigator.serviceWorker.ready would never resolve here,
    // so we wait on the registration's own worker reaching 'activated'.
    var reg = await navigator.serviceWorker.register(PROXY + '/sw.js');
    if (!reg.active) {
      await new Promise(function (resolve) {
        var sw = reg.installing || reg.waiting;
        if (!sw) return resolve();
        sw.addEventListener('statechange', function () {
          if (sw.state === 'activated') resolve();
        });
      });
    }
    return reg;
  }
  async function existingSub() {
    var reg = await registerSw();
    return reg.pushManager.getSubscription();
  }
  async function ensureSubscribed() {
    var reg = await registerSw();
    var sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8(VAPID),
      });
    }
    var json = sub.toJSON();
    await fetch(PROXY + '/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: sub.endpoint,
        keys: json.keys,
        page: location.pathname,
        locale: navigator.language,
        customer_id: CUSTOMER_ID || undefined,
      }),
    });
    return sub;
  }
  async function watchProduct(productId) {
    var sub = await ensureSubscribed();
    await fetch(PROXY + '/product-subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint, product_id: String(productId) }),
    });
    return true;
  }
  async function trackView(productId) {
    try {
      if (Notification.permission !== 'granted') return;
      var sub = await existingSub();
      if (!sub) return;
      await fetch(PROXY + '/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint, product_id: String(productId) }),
        keepalive: true,
      });
    } catch (e) {}
  }
  function trackEvent(trigger, action, detail) {
    try {
      var page = detail ? (location.pathname + ' | ' + detail) : location.pathname;
      fetch(PROXY + '/prompt-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trigger: trigger, action: action, page: page.slice(0, 2048) }),
        keepalive: true,
      });
    } catch (e) {}
  }

  // Public API for the theme/merchant to wire to custom buttons (unchanged).
  window.KreoWebNoti = {
    isSupported: function () { return supported; },
    permission: function () { return supported ? Notification.permission : 'unsupported'; },
    subscribe: function () { return ensureSubscribed(); },
    watchProduct: watchProduct,
  };

  // ---- audience + caps (pure) ----------------------------------------------
  function isMobile() {
    try { return window.matchMedia('(max-width: 768px)').matches; }
    catch (e) { return /Mobi|Android/i.test(navigator.userAgent); }
  }
  function isReturning() {
    try { return !!localStorage.getItem(K_SEEN); } catch (e) { return false; }
  }
  function markSeen() {
    try { localStorage.setItem(K_SEEN, '1'); } catch (e) {}
  }
  function audienceMatches(aud) {
    if (!aud) return true;
    if (aud.visitors === 'logged_in' && !CUSTOMER_ID) return false;
    if (aud.visitors === 'guest' && CUSTOMER_ID) return false;
    if (aud.device === 'mobile' && !isMobile()) return false;
    if (aud.device === 'desktop' && isMobile()) return false;
    if (aud.recency === 'new' && isReturning()) return false;
    if (aud.recency === 'returning' && !isReturning()) return false;
    return true;
  }
  function capBlocked(reprompt) {
    var perm = Notification.permission;
    if (perm === 'granted' || perm === 'denied') return true;
    try {
      var now = Date.now();
      var snooze = parseInt(localStorage.getItem(K_SNOOZE) || '0', 10);
      if (snooze && now < snooze) return true;
      var dismissed = parseInt(localStorage.getItem(K_DISMISS) || '0', 10);
      var maxDismiss = reprompt.max_dismissals || 3;
      var maxSession = reprompt.max_per_session || 1;
      if (dismissed >= maxDismiss) return true;
      var session = parseInt(sessionStorage.getItem(K_SESSION) || '0', 10);
      if (session >= maxSession) return true;
    } catch (e) {}
    return false;
  }
  function recordShown() {
    try {
      var n = parseInt(sessionStorage.getItem(K_SESSION) || '0', 10) + 1;
      sessionStorage.setItem(K_SESSION, String(n));
    } catch (e) {}
  }
  function recordDismiss(reprompt) {
    try {
      var n = parseInt(localStorage.getItem(K_DISMISS) || '0', 10) + 1;
      localStorage.setItem(K_DISMISS, String(n));
      localStorage.setItem(K_SNOOZE, String(Date.now() + reprompt.snooze_days * 86400000));
    } catch (e) {}
  }
  function recordAccept() {
    try { localStorage.removeItem(K_SNOOZE); } catch (e) {}
  }

  // ---- UI bar ---------------------------------------------------------------
  // The bar renders inside a shadow root: storefront CSS can't reach in and break
  // it, and we get real media queries (a style attribute can't hold one).
  //
  // The host is a full-width centering strip. It deliberately does NOT use
  // left:50% + translateX(-50%): for a fixed box with left:50% and width:auto,
  // shrink-to-fit is given (100vw - 50vw) of available width, so the bar could
  // never exceed half the viewport and the max-width:92vw never bound. On a 390px
  // phone that forced it to min-content and wrapped the message one word per line
  // (measured: 243px box, 50px text column, 9 lines). Centering via the host
  // instead makes the available width the full viewport minus the gutters.
  //
  // Class names are namespaced so the no-shadow-DOM fallback (append into the
  // host itself) can never leak these rules onto the storefront.
  var BAR_CSS = [
    '.kwn-bar,.kwn-bar *{box-sizing:border-box}',
    '.kwn-bar{',
    'pointer-events:auto;display:grid;grid-template-columns:1fr auto auto;align-items:center;',
    'gap:10px 12px;max-width:min(560px,100%);padding:12px 12px 12px 16px;border-radius:14px;',
    'background:#15131b;border:1px solid rgba(255,255,255,.10);color:#efecf5;',
    'box-shadow:0 12px 40px -8px rgba(10,6,20,.55);',
    'font:400 14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;',
    'animation:kwn-rise .34s cubic-bezier(.22,1,.36,1) both}',
    '@keyframes kwn-rise{from{opacity:0;transform:translate3d(0,10px,0)}to{opacity:1;transform:none}}',
    '@media (prefers-reduced-motion:reduce){.kwn-bar{animation:none}}',
    '.kwn-msg{margin:0;text-wrap:pretty}',
    '.kwn-bar button{appearance:none;-webkit-appearance:none;margin:0;border:0;font:inherit;cursor:pointer}',
    '.kwn-bar button:focus-visible{outline:2px solid #a78bfa;outline-offset:2px}',
    '.kwn-yes{background:#fff;color:#15131b;border-radius:9px;padding:0 14px;min-height:40px;',
    'font-weight:600;white-space:nowrap;transition:background .18s ease}',
    '.kwn-yes:hover{background:#e4dff0}',
    '.kwn-yes[disabled]{opacity:.55;cursor:default}',
    '.kwn-no{background:transparent;color:#8d8798;display:grid;place-items:center;width:40px;',
    'height:40px;border-radius:9px;transition:background .18s ease,color .18s ease}',
    '.kwn-no:hover{background:rgba(255,255,255,.08);color:#efecf5}',
    '.kwn-no svg{display:block;width:15px;height:15px}',
    // Phones: message on its own line, full-width primary action beneath it, and
    // a 44px dismiss target parked at the top-right.
    '@media (max-width:560px){',
    '.kwn-bar{grid-template-columns:1fr auto;font-size:15px;padding:14px;gap:12px}',
    '.kwn-msg{grid-column:1;grid-row:1}',
    '.kwn-no{grid-column:2;grid-row:1;align-self:start;width:44px;height:44px;margin:-7px -7px 0 0}',
    '.kwn-yes{grid-column:1/-1;grid-row:2;width:100%;min-height:46px}}',
  ].join('');

  var X_SVG =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

  /** Build an opt-in bar. Returns a handle the caller mutates; o.onAccept and
   *  o.onDismiss receive it. The click handlers stay synchronous so the accept
   *  keeps its transient user activation (the native permission prompt needs it). */
  function buildBar(o) {
    var host = document.createElement('div');
    // pointer-events:none so this full-width strip never eats storefront clicks;
    // the bar itself opts back in.
    host.style.cssText =
      'position:fixed;left:0;right:0;bottom:calc(12px + env(safe-area-inset-bottom,0px));' +
      'z-index:2147483000;display:flex;justify-content:center;padding:0 12px;margin:0;' +
      'box-sizing:border-box;pointer-events:none';
    var root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : host;
    var style = document.createElement('style');
    style.textContent = BAR_CSS;
    root.appendChild(style);

    var bar = document.createElement('div');
    bar.className = 'kwn-bar';
    bar.setAttribute('role', 'dialog');
    bar.setAttribute('aria-label', 'Browser notifications');

    var msg = document.createElement('p');
    msg.className = 'kwn-msg';
    msg.textContent = o.message;

    var yes = document.createElement('button');
    yes.className = 'kwn-yes';
    yes.type = 'button';
    yes.textContent = o.acceptLabel;

    var no = document.createElement('button');
    no.className = 'kwn-no';
    no.type = 'button';
    no.setAttribute('aria-label', 'Dismiss');
    no.innerHTML = X_SVG; // constant markup, no interpolation

    bar.appendChild(msg); bar.appendChild(yes); bar.appendChild(no);
    root.appendChild(bar);

    var api = {
      host: host,
      close: function () {
        try { host.remove(); } catch (e) {}
        if (activeBar === host) activeBar = null;
      },
      setMessage: function (t) { msg.textContent = t; },
      setAccept: function (t) { yes.textContent = t; },
      disableAccept: function (d) { yes.disabled = !!d; },
      dropAccept: function () { try { yes.remove(); } catch (e) {} },
    };
    yes.onclick = function () { o.onAccept(api); };
    no.onclick = function () { o.onDismiss(api); };
    return api;
  }

  /** Show the opt-in confirm pill for a trigger. The Accept click is the user
   *  gesture that lets ensureSubscribed() fire the native permission prompt.
   *  opts.force lets a gesture-driven trigger (out_of_stock_submit, fired from a
   *  real form submit) show even if an earlier bar already used the per-load
   *  budget — caps are still enforced, and mountBar guarantees no stacking. */
  function showPrompt(triggerKey, trig, reprompt, opts) {
    var force = !!(opts && opts.force);
    if (prompted && !force) return;
    if (capBlocked(reprompt)) return;
    prompted = true;
    recordShown();
    trackEvent(triggerKey, 'shown');

    var ui = buildBar({
      message: trig.copy.prompt,
      acceptLabel: trig.copy.accept,
      onAccept: function (api) {
        api.close();
        trackEvent(triggerKey, 'accept_click');
        ensureSubscribed()
          .then(function () { recordAccept(); trackEvent(triggerKey, 'accepted'); })
          .catch(function (e) { trackEvent(triggerKey, 'accept_error', (e && (e.name + ': ' + e.message)) || 'unknown'); });
      },
      onDismiss: function (api) {
        api.close();
        recordDismiss(reprompt);
        trackEvent(triggerKey, 'dismissed');
      },
    });
    mountBar(ui.host);
  }

  /** Sold-out product watch button (existing behavior, gated by master switch). */
  function showWatchButton() {
    if (!PRODUCT_SOLD_OUT) return false;
    prompted = true; // don't stack a generic opt-in on top of the watch CTA
    var ui = buildBar({
      message: 'Out of stock — get notified when it’s back?',
      acceptLabel: 'Notify me',
      onAccept: function (api) {
        api.disableAccept(true); api.setAccept('…');
        watchProduct(PRODUCT_ID).then(function () {
          api.setMessage('✓ We’ll notify you when it’s back');
          api.dropAccept();
        }).catch(function () { api.disableAccept(false); api.setAccept('Notify me'); });
      },
      onDismiss: function (api) { api.close(); },
    });
    mountBar(ui.host);
    return true;
  }

  // ---- trigger arming -------------------------------------------------------
  function isThankYouPage() {
    if (window.Shopify && window.Shopify.Checkout) return true;
    var path = location.pathname;
    if (/\/thank_you/.test(path)) return true;
    // Guest order-status URLs look like /orders/{token}; exclude logged-in
    // account order pages (/account/orders/...).
    if (/\/orders\//.test(path) && !/\/account\//.test(path)) return true;
    return false;
  }

  // Post-purchase deep link: a "get order updates" link in the confirmation
  // email/WhatsApp points at a theme page with ?webnoti=thankyou, so the ask can
  // run where push is actually allowed (the order-status page itself is sandboxed).
  function fromThankYouLink() {
    try { return new URLSearchParams(location.search).get('webnoti') === 'thankyou'; }
    catch (e) { return /(^|[?&])webnoti=thankyou(&|$)/.test(location.search); }
  }

  function armOutOfStock(trig, reprompt) {
    var fired = false;
    function check() {
      if (fired) return;
      // The theme's success alert starts [hidden]; on a successful submit the
      // attribute is removed. Match only the visible one.
      var el = document.querySelector('[data-bis-success]:not([hidden])');
      if (el) {
        fired = true;
        obs.disconnect();
        showPrompt('out_of_stock_submit', trig, reprompt, { force: true });
      }
    }
    var obs = new MutationObserver(check);
    obs.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
    check(); // in case a success alert is already visible at arm time
  }

  function applyConfig(config) {
    if (!config || config.master_enabled === false) return;
    var reprompt = config.reprompt || { snooze_days: 7, max_per_session: 1, max_dismissals: 3 };
    var trg = config.triggers || {};

    if (PRODUCT_ID) trackView(PRODUCT_ID);

    // Arm the out-of-stock trigger FIRST and independently of the sold-out watch
    // bar, so it observes the theme back-in-stock form's success on the exact
    // page (a sold-out PDP) it is meant for. It uses force + mountBar, so it can
    // still fire after the shopper submits the form without stacking on the bar.
    var oos = trg.out_of_stock_submit;
    if (oos && oos.enabled && audienceMatches(oos.audience)) armOutOfStock(oos, reprompt);

    // Sold-out watch bar (existing per-product web-push watch). Shows on load and
    // occupies the single-prompt budget so the ambient triggers don't stack on it.
    showWatchButton();

    var ty = trg.thank_you_page;
    var tyLink = fromThankYouLink();
    if (ty && ty.enabled && audienceMatches(ty.audience) && (isThankYouPage() || tyLink)) {
      // tyLink = arrived via the post-purchase link on a normal theme page; give
      // the page a beat to settle, then force past the per-load guard. Caps and
      // permission checks in showPrompt still apply.
      setTimeout(function () {
        showPrompt('thank_you_page', ty, reprompt, tyLink ? { force: true } : undefined);
      }, tyLink ? 600 : (ty.delay_ms || 0));
    }

    var dwell = trg.dwell_time;
    if (dwell && dwell.enabled && audienceMatches(dwell.audience)) {
      setTimeout(function () { showPrompt('dwell_time', dwell, reprompt); }, (dwell.threshold_seconds || 60) * 1000);
    }

    // Known-customer trigger: only for logged-in Shopify customers (identified);
    // scope 'purchasers' further restricts to those with >= 1 order.
    var kc = trg.known_customer;
    if (kc && kc.enabled && CUSTOMER_ID && (kc.scope !== 'purchasers' || ORDERS_COUNT > 0)) {
      setTimeout(function () { showPrompt('known_customer', kc, reprompt); }, (kc.threshold_seconds || 25) * 1000);
    }
  }

  // ---- subscription reconciliation ------------------------------------------
  // A browser that has already granted permission still holds its PushSubscription,
  // but the row backing it lives in whichever backend the App Proxy pointed at on
  // the day the shopper opted in. Re-POSTing that subscription lets the CURRENT
  // backend re-learn a subscriber it never recorded — getSubscription() returns
  // the endpoint and keys the browser already has, so this shows no prompt and
  // mints nothing new. It is purely restorative: if the browser has no
  // subscription we leave it alone and let the normal triggers do their job.
  //
  // Throttled to once a day per browser so recovery costs one request, not one
  // per page view. No localStorage (private mode, storage disabled) means no way
  // to throttle, so we skip rather than hammer /subscribe on every load.
  var K_RECONCILED = 'webnoti_reconciled_at';
  var RECONCILE_EVERY_MS = 86400000;
  function reconcileDue() {
    try {
      var last = parseInt(localStorage.getItem(K_RECONCILED) || '0', 10);
      return !last || Date.now() - last >= RECONCILE_EVERY_MS;
    } catch (e) {
      return false;
    }
  }
  async function reconcile() {
    if (Notification.permission !== 'granted' || !reconcileDue()) return;
    try {
      var reg = await registerSw();
      var sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      var json = sub.toJSON();
      await fetch(PROXY + '/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: json.keys,
          page: location.pathname,
          locale: navigator.language,
          customer_id: CUSTOMER_ID || undefined,
        }),
      });
      try { localStorage.setItem(K_RECONCILED, String(Date.now())); } catch (e) {}
    } catch (e) {}
  }

  async function init() {
    markSeenAfterRead();
    if (Notification.permission === 'denied') return;
    // Fire-and-forget, and deliberately independent of the trigger config: this
    // repairs the record of a consent the shopper already gave, so it must still
    // run if /config is unreachable or the merchant has paused new prompts.
    reconcile();
    try {
      var res = await fetch(PROXY + '/config', { credentials: 'omit' });
      if (!res.ok) return; // no config / gated — do nothing (safe default)
      var config = await res.json();
      applyConfig(config);
    } catch (e) {}
  }

  // Read recency BEFORE marking this visit, so "returning" is correct this load.
  var wasReturning = isReturning();
  function markSeenAfterRead() { markSeen(); }
  // Patch audienceMatches to use the snapshot taken at load.
  var _isReturning = isReturning;
  isReturning = function () { return wasReturning; };
  void _isReturning;

  // Diagnostic harness — only runs when the URL has ?webnoti=debug, so real
  // visitors never see it. Registers the SW, then on a real click attempts the
  // subscribe flow and logs the exact failure point + error to the console.
  var DBG = /(^|[?&])webnoti=debug(&|$)/.test(location.search);
  async function debugRun() {
    var log = function () { try { console.log.apply(console, ['[webnoti]'].concat([].slice.call(arguments))); } catch (e) {} };
    var err = function () { try { console.error.apply(console, ['[webnoti]'].concat([].slice.call(arguments))); } catch (e) {} };
    log('DEBUG. supported =', supported, '| Notification.permission =', (typeof Notification !== 'undefined' ? Notification.permission : 'n/a'), '| VAPID set =', !!VAPID);
    if (!supported) { err('Browser lacks serviceWorker/PushManager/Notification — cannot subscribe here.'); return; }
    if (Notification.permission === 'denied') { err('Notification.permission is DENIED for this site → the browser will NEVER show the Allow/Block prompt. Reset site notifications to "Ask" (padlock/site-settings) or use a truly fresh profile, then retry.'); }
    var reg;
    try {
      log('registering SW:', PROXY + '/sw.js');
      reg = await navigator.serviceWorker.register(PROXY + '/sw.js');
      log('SW registered OK. scope =', reg.scope, '| active =', !!reg.active);
    } catch (e) {
      err('SW REGISTER FAILED:', e && e.name, '-', e && e.message, e);
      return;
    }
    var btn = document.createElement('button');
    btn.textContent = '🔔 webnoti debug — click to subscribe';
    btn.style.cssText = 'position:fixed;bottom:20px;left:20px;z-index:2147483647;padding:14px 18px;background:#c00;color:#fff;font:600 15px sans-serif;border:0;border-radius:10px;cursor:pointer;box-shadow:0 8px 30px rgba(0,0,0,.4)';
    btn.onclick = async function () {
      log('clicked. permission BEFORE =', Notification.permission);
      try {
        var sub = await reg.pushManager.getSubscription();
        log('existing subscription =', !!sub);
        if (!sub) {
          log('calling pushManager.subscribe() — the native Allow/Block prompt should appear NOW...');
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(VAPID) });
        }
        log('SUBSCRIBE OK. permission AFTER =', Notification.permission, '| endpoint =', sub.endpoint.slice(0, 48) + '…');
        var j = sub.toJSON();
        var r = await fetch(PROXY + '/subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ endpoint: sub.endpoint, keys: j.keys, page: location.pathname, locale: navigator.language, customer_id: CUSTOMER_ID || undefined }) });
        log('POST /subscribe status =', r.status, r.status === 200 ? '✓ you should now be a subscriber' : '(NON-200!)');
      } catch (e) {
        err('SUBSCRIBE FAILED:', e && e.name, '-', e && e.message, e);
        err('NotAllowedError => the browser blocked/denied the prompt (site notifications = Block, or the prompt was dismissed).');
      }
    };
    document.body.appendChild(btn);
    log('Debug button added (bottom-left, red). Click it to run the subscribe flow.');
  }

  function schedule() { if (DBG) return void debugRun(); init(); }
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule);
})();
